/**
 * ReportImageSlot — Editable image placeholder for the report.
 * Three fields per slot: image_url, alt, caption. Stored in field_edits
 * under the provided fieldKeyPrefix (e.g. "intro.atmosphere.0").
 *
 * Saavutettavuus: alt-text and caption are separate per käsikirjoitus 2026-05
 * ("saavutettavuuden takia kuva + teksti erikseen olisi hyvä").
 *
 * If image_url is empty, renders an inline placeholder hinting the admin
 * to add a URL via edit mode. Captions render only when filled.
 */
import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Image as ImageIcon } from 'lucide-react';
import { SITRA } from './reportStyles';
import EditableText from './EditableText';
import { useReportEdits } from './ReportEditsContext';
import ImageLightbox from './ImageLightbox';
import { normalizeImageUrl } from './imageUrl';

const Figure = styled.figure`
  margin: 24px 0;
  padding: 0;
`;

const Frame = styled.div`
  /* When a contain-fit image is loaded, drop the fixed aspect so the
     container shrink-wraps to the image's natural dimensions (no letter-
     boxing). The aspect-ratio constraint only matters for the placeholder
     state and for cover-fit images that need a fixed crop area. */
  aspect-ratio: ${p => (p.$hasImage && p.$fit === 'contain') ? 'auto' : (p.$aspect || '16 / 9')};
  background: ${SITRA.surfaceMuted};
  border: 1px solid ${SITRA.border};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${SITRA.textMuted};
  overflow: hidden;
  img {
    width: 100%;
    height: ${p => (p.$hasImage && p.$fit === 'contain') ? 'auto' : '100%'};
    display: block;
    object-fit: ${p => p.$fit || 'cover'};
  }
`;

const PlaceholderHint = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  text-align: center;
  padding: 20px;
`;

const CaptionRow = styled.figcaption`
  margin-top: 10px;
  font-size: 0.85rem;
  color: ${SITRA.textMuted};
  font-style: italic;
  line-height: 1.5;
`;

const AdminMeta = styled.div`
  margin-top: 6px;
  font-size: 0.7rem;
  color: ${SITRA.textMuted};
  @media print { display: none; }
`;

export default function ReportImageSlot({
  fieldKeyPrefix,
  label = 'Kuva',
  aspect = '16 / 9',
  hint = 'Kuvapaikka — lisää URL admin-tilassa',
  // 'cover' (default) crops the image to fill the frame — fine for full-
  // bleed editorial photography. 'contain' shows the whole image with
  // letterboxing — required for design assets like the scenario summary
  // cards where any crop loses meaning.
  fit = 'cover',
  // When set, clicking the image fires this with `{ url, alt }`. Used by
  // groups that manage their own gallery (e.g. TulevaisuuskuvatGroup) to
  // open ImageLightbox at the clicked image's index across multiple slots.
  // For standalone slots, leaving this undefined falls back to the built-
  // in single-image lightbox — every report image gets click-to-zoom for
  // free without per-callsite plumbing.
  onImageClick,
}) {
  const { get, canEdit, editMode } = useReportEdits();
  // Normalise here so any Drive viewer URL pasted by a fasilitaattori
  // ("Copy link" → /file/d/<id>/view) gets rewritten to the lh3 CDN form
  // before it hits <img src>. Idempotent on already-normalised URLs.
  const url = normalizeImageUrl(get(`${fieldKeyPrefix}.image_url`, ''));
  const alt = get(`${fieldKeyPrefix}.alt`, '');
  const caption = get(`${fieldKeyPrefix}.caption`, '');
  const showAdminMeta = canEdit && editMode;
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Public viewers shouldn't see empty placeholder boxes — those are
  // an admin affordance ("you can put an image here"). If there's no
  // image AND we're not in edit mode, render nothing.
  if (!url && !editMode) return null;

  // Two click modes:
  //   1. Caller provided onImageClick → use it (group lightbox).
  //   2. No callback → open the built-in single-image lightbox.
  const onImg = url
    ? () => {
        if (onImageClick) onImageClick({ url, alt: alt || label });
        else setLightboxOpen(true);
      }
    : undefined;
  const imgStyle = onImg ? { cursor: 'zoom-in' } : undefined;

  return (
    <Figure>
      <Frame $aspect={aspect} $fit={fit} $hasImage={!!url}>
        {url
          ? <img src={url} alt={alt || label} onClick={onImg} style={imgStyle} />
          : <PlaceholderHint>
              <ImageIcon size={28} strokeWidth={1.2} />
              <div>{hint}</div>
            </PlaceholderHint>}
      </Frame>
      {lightboxOpen && (
        <ImageLightbox
          images={[{ url, alt: alt || label, caption: caption || alt || label }]}
          initialIndex={0}
          onClose={() => setLightboxOpen(false)}
        />
      )}
      {(caption || showAdminMeta) && (
        <CaptionRow>
          <EditableText
            fieldKey={`${fieldKeyPrefix}.caption`}
            fallback={caption || ''}
            label={`${label} — kuvateksti`}
          />
        </CaptionRow>
      )}
      {showAdminMeta && (
        <AdminMeta>
          <strong>URL:</strong>{' '}
          <EditableText
            fieldKey={`${fieldKeyPrefix}.image_url`}
            fallback="—"
            label={`${label} — kuvan URL`}
            multiline={false}
          />
          {' · '}
          <strong>Alt:</strong>{' '}
          <EditableText
            fieldKey={`${fieldKeyPrefix}.alt`}
            fallback="—"
            label={`${label} — alt-teksti`}
            multiline={false}
          />
        </AdminMeta>
      )}
    </Figure>
  );
}
